import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSelector from './LanguageSelector';

type View = 'login' | 'register' | 'forgot';

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirm?: string;
}

export default function AuthModal() {
  const { isAuthModalOpen, closeAuthModal, signInWithEmail, signUpWithEmail, resetPassword, authError, clearAuthError } = useAuth();
  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [isAdminAccess, setIsAdminAccess] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { clearAuthError(); closeAuthModal(); }
    };
    if (isAuthModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      setView('login');
      resetForm();
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isAuthModalOpen, closeAuthModal, clearAuthError]);

  const resetForm = () => {
    setEmail(''); setPassword(''); setName(''); setConfirm('');
    setErrors({}); setServerError(''); setSuccess(''); setLoading(false);
    setShowPass(false); setShowConfirm(false); setIsAdminAccess(false);
  };

  // Fecha o modal limpando também a mensagem de erro vinda do contexto
  // (ex.: bloqueio de login social para admin/instrutor).
  const handleClose = () => { clearAuthError(); closeAuthModal(); };

  const switchView = (v: View) => { setView(v); setErrors({}); setServerError(''); setSuccess(''); clearAuthError(); };

  const validateLogin = () => {
    const e: FormErrors = {};
    if (!email.trim()) e.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'E-mail inválido';
    if (!password) e.password = 'Senha é obrigatória';
    return e;
  };

  const validateRegister = () => {
    const e: FormErrors = {};
    if (!name.trim() || name.trim().length < 3) e.name = 'Nome completo (mín. 3 caracteres)';
    if (!email.trim()) e.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'E-mail inválido';
    if (!password || password.length < 8) e.password = 'Senha deve ter pelo menos 8 caracteres';
    else if (!/[A-Z]/.test(password)) e.password = 'Inclua pelo menos uma letra maiúscula';
    else if (!/[0-9]/.test(password)) e.password = 'Inclua pelo menos um número';
    if (password !== confirm) e.confirm = 'As senhas não coincidem';
    return e;
  };

  // Login ÚNICO para todos os perfis. A autenticação é sempre real (Supabase).
  // O estado da caixa "Acesso Restrito" (isAdminAccess) é enviado ao AuthContext,
  // que aplica a trava: admin/instrutor só entram com a caixa marcada; usuário
  // comum só entra com a caixa desmarcada. NÃO há mais credenciais no código.
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearAuthError();
    const errs = validateLogin();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true); setServerError('');
    const { error } = await signInWithEmail(email.trim(), password, isAdminAccess);
    setLoading(false);
    if (error) setServerError(error);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateRegister();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true); setServerError('');
    const { error } = await signUpWithEmail(email.trim(), password, name.trim());
    setLoading(false);
    if (error) setServerError(error);
    else setSuccess('Conta criada! Verifique seu e-mail para confirmar o cadastro.');
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!email.trim()) errs.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'E-mail inválido';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true); setServerError('');
    const { error } = await resetPassword(email.trim());
    setLoading(false);
    if (error) setServerError(error);
    else setSuccess('Link de recuperação enviado para seu e-mail!');
  };

  if (!isAuthModalOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-[#0F1419] border border-white/10 rounded-2xl w-full max-w-md p-8 animate-[fadeInUp_0.3s_ease] max-h-[90vh] overflow-y-auto">
        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/10"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {/* Language Selector — top left of modal */}
        <div className="absolute top-4 left-4">
          <LanguageSelector variant="light" size="compact" />
        </div>

        {/* Logo */}
        <div className="text-center mb-6">
          <img
            src="/images/logotipo.jpg"
            alt="Logo"
            className="h-10 w-auto mx-auto mb-4"
          />
          {view === 'login' && <>
            <h2 className="text-xl font-bold text-white mb-1">Acesse sua conta</h2>
            <p className="text-gray-400 text-sm">Entre com seu e-mail e senha</p>
          </>}
          {view === 'register' && <>
            <h2 className="text-xl font-bold text-white mb-1">Criar sua conta</h2>
            <p className="text-gray-400 text-sm">Cadastre-se para acessar os cursos</p>
          </>}
          {view === 'forgot' && <>
            <h2 className="text-xl font-bold text-white mb-1">Recuperar senha</h2>
            <p className="text-gray-400 text-sm">Enviaremos um link para seu e-mail</p>
          </>}
        </div>

        {/* Tab switcher: login/register */}
        {view !== 'forgot' && (
          <div className="flex p-1 bg-white/5 rounded-xl mb-6 border border-white/10">
            <button
              onClick={() => switchView('login')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${view === 'login' ? 'bg-[#D4AF37] text-[#0A0A0F]' : 'text-gray-400 hover:text-white'}`}
            >
              Entrar
            </button>
            <button
              onClick={() => switchView('register')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${view === 'register' ? 'bg-[#D4AF37] text-[#0A0A0F]' : 'text-gray-400 hover:text-white'}`}
            >
              Criar Conta
            </button>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="mb-4 p-3 bg-green-500/15 border border-green-500/30 rounded-lg flex items-start gap-2">
            <i className="ri-checkbox-circle-line text-green-400 mt-0.5"></i>
            <p className="text-sm text-green-300">{success}</p>
          </div>
        )}

        {/* Server / auth error */}
        {(serverError || authError) && (
          <div className="mb-4 p-3 bg-red-500/15 border border-red-500/30 rounded-lg flex items-start gap-2">
            <i className="ri-error-warning-line text-red-400 mt-0.5"></i>
            <p className="text-sm text-red-300">{serverError || authError}</p>
          </div>
        )}

        {/* LOGIN FORM */}
        {view === 'login' && !success && (
          <>
            <form onSubmit={handleLogin} className="space-y-4 mb-5" noValidate>
              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1.5">E-mail</label>
                <div className={`flex items-center gap-2 bg-white/5 border rounded-lg px-3 py-2.5 transition-colors ${errors.email ? 'border-red-500/60' : 'border-white/10 focus-within:border-[#D4AF37]/50'}`}>
                  <span className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-gray-400 text-sm"></i>
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors(p => ({ ...p, email: undefined })); }}
                    placeholder="seu@email.com"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                    autoComplete="email"
                  />
                </div>
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1.5">Senha</label>
                <div className={`flex items-center gap-2 bg-white/5 border rounded-lg px-3 py-2.5 transition-colors ${errors.password ? 'border-red-500/60' : 'border-white/10 focus-within:border-[#D4AF37]/50'}`}>
                  <span className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-lock-line text-gray-400 text-sm"></i>
                  </span>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: undefined })); }}
                    placeholder="••••••••"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                    autoComplete="current-password"
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="w-4 h-4 flex items-center justify-center cursor-pointer text-gray-400 hover:text-white">
                    <i className={`${showPass ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password}</p>}
                {!isAdminAccess && (
                  <div className="text-right mt-1.5">
                    <button type="button" onClick={() => switchView('forgot')} className="text-xs text-[#D4AF37] hover:underline cursor-pointer">
                      Esqueci minha senha
                    </button>
                  </div>
                )}
              </div>

              {/* Restricted access checkbox */}
              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer group select-none">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={isAdminAccess}
                      onChange={(e) => setIsAdminAccess(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${isAdminAccess ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/20 bg-white/5 group-hover:border-white/40'}`}>
                      {isAdminAccess && <i className="ri-check-line text-[#0A0A0F] text-[10px]"></i>}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 group-hover:text-gray-400 transition-colors flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-shield-keyhole-line text-[10px]"></i></span>
                    Acesso Restrito
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-green-500 hover:bg-green-400 disabled:bg-green-500/50 rounded-lg text-white font-bold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {loading
                  ? <><i className="ri-loader-4-line animate-spin"></i> Verificando...</>
                  : isAdminAccess
                    ? <><i className="ri-shield-keyhole-line"></i> Entrar em Área Restrita</>
                    : 'Entrar com E-mail'
                }
              </button>
            </form>

          </>
        )}

        {/* REGISTER FORM */}
        {view === 'register' && !success && (
          <>
            <form onSubmit={handleRegister} className="space-y-4 mb-5" noValidate>
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1.5">Nome completo</label>
                <div className={`flex items-center gap-2 bg-white/5 border rounded-lg px-3 py-2.5 transition-colors ${errors.name ? 'border-red-500/60' : 'border-white/10 focus-within:border-[#D4AF37]/50'}`}>
                  <span className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-user-line text-gray-400 text-sm"></i>
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }}
                    placeholder="Seu nome completo"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                    autoComplete="name"
                  />
                </div>
                {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1.5">E-mail</label>
                <div className={`flex items-center gap-2 bg-white/5 border rounded-lg px-3 py-2.5 transition-colors ${errors.email ? 'border-red-500/60' : 'border-white/10 focus-within:border-[#D4AF37]/50'}`}>
                  <span className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-gray-400 text-sm"></i>
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors(p => ({ ...p, email: undefined })); }}
                    placeholder="seu@email.com"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                    autoComplete="email"
                  />
                </div>
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1.5">Senha</label>
                <div className={`flex items-center gap-2 bg-white/5 border rounded-lg px-3 py-2.5 transition-colors ${errors.password ? 'border-red-500/60' : 'border-white/10 focus-within:border-[#D4AF37]/50'}`}>
                  <span className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-lock-line text-gray-400 text-sm"></i>
                  </span>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrors(p => ({ ...p, password: undefined })); }}
                    placeholder="Mín. 8 caracteres"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="w-4 h-4 flex items-center justify-center cursor-pointer text-gray-400 hover:text-white">
                    <i className={`${showPass ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password}</p>}
                {/* Strength indicator */}
                {password && (
                  <div className="flex gap-1 mt-2">
                    {[
                      password.length >= 8,
                      /[A-Z]/.test(password),
                      /[0-9]/.test(password),
                      /[^A-Za-z0-9]/.test(password),
                    ].map((ok, i) => (
                      <div key={i} className={`flex-1 h-1 rounded-full transition-colors ${ok ? 'bg-green-500' : 'bg-white/15'}`}></div>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1.5">Confirmar senha</label>
                <div className={`flex items-center gap-2 bg-white/5 border rounded-lg px-3 py-2.5 transition-colors ${errors.confirm ? 'border-red-500/60' : 'border-white/10 focus-within:border-[#D4AF37]/50'}`}>
                  <span className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-lock-password-line text-gray-400 text-sm"></i>
                  </span>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setErrors(p => ({ ...p, confirm: undefined })); }}
                    placeholder="Repita a senha"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="w-4 h-4 flex items-center justify-center cursor-pointer text-gray-400 hover:text-white">
                    <i className={`${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                  </button>
                </div>
                {errors.confirm && <p className="text-xs text-red-400 mt-1">{errors.confirm}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-green-500 hover:bg-green-400 disabled:bg-green-500/50 rounded-lg text-white font-bold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {loading ? <><i className="ri-loader-4-line animate-spin"></i> Criando conta...</> : 'Criar Conta Grátis'}
              </button>
            </form>

          </>
        )}

        {/* FORGOT PASSWORD */}
        {view === 'forgot' && !success && (
          <form onSubmit={handleForgot} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1.5">E-mail cadastrado</label>
              <div className={`flex items-center gap-2 bg-white/5 border rounded-lg px-3 py-2.5 transition-colors ${errors.email ? 'border-red-500/60' : 'border-white/10 focus-within:border-[#D4AF37]/50'}`}>
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-gray-400 text-sm"></i>
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors(p => ({ ...p, email: undefined })); }}
                  placeholder="seu@email.com"
                  className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                />
              </div>
              {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#D4AF37] hover:bg-[#C49D2E] disabled:bg-[#D4AF37]/50 rounded-lg text-[#0A0A0F] font-bold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? <><i className="ri-loader-4-line animate-spin"></i> Enviando...</> : 'Enviar Link de Recuperação'}
            </button>

            <button type="button" onClick={() => switchView('login')} className="w-full text-center text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
              ← Voltar para o login
            </button>
          </form>
        )}

        {success && view === 'forgot' && (
          <button onClick={() => switchView('login')} className="w-full mt-4 text-center text-sm text-[#D4AF37] hover:underline cursor-pointer">
            ← Voltar para o login
          </button>
        )}

        {/* Footer */}
        {view !== 'forgot' && (
          <p className="text-center text-xs text-gray-500 mt-6">
            Ao entrar, você concorda com nossos{' '}
            <a href="/termos-de-uso" className="text-[#D4AF37] hover:underline cursor-pointer">Termos de Uso</a>{' '}
            e{' '}
            <a href="/politica-de-privacidade" className="text-[#D4AF37] hover:underline cursor-pointer">Política de Privacidade</a>.
          </p>
        )}
      </div>
    </div>
  );
}

/*
 * LOGIN SOCIAL REMOVIDO POR INTEIRO (2026-08-18).
 *
 * Aqui viviam três botões — Google, Facebook e Apple — nesta que é a
 * principal porta de entrada do site. Nenhum dos três funcionava:
 *
 *   • Google e Facebook: conferido no endpoint público /auth/v1/settings do
 *     projeto — "google": false, "facebook": false. Nenhum provider OAuth foi
 *     habilitado no Supabase; só "email": true. O clique só podia terminar em
 *     erro de OAuth, e num modal de login isso não é bug de canto: é o custo
 *     de conversão na porta.
 *   • Apple: nem provider tinha. Era `onClick={() => alert('Login com Apple
 *     em breve!')}` — caixa do navegador, mesma família dos botões de
 *     App Store e Google Play que prometiam aplicativo inexistente.
 *
 * O divisor "ou continue com" / "ou cadastre-se com" saiu junto: sem os
 * botões, ele anunciava uma alternativa que não existe.
 *
 * Volta quando um provider for configurado de verdade no painel do Supabase —
 * e o `signInWithGoogle` / `signInWithFacebook` continuam no AuthContext,
 * prontos, esperando exatamente isso.
 */